/**
 * Wire shape for `POST /api/v4/admin/blueprints/:kind/test` — plan
 * 2026-07-30-004 (Unit 5, "Full-slot `test` + checks strip").
 *
 * Canonical location for the response schema: the server
 * (`testBlueprintCardSlots` in `src/services/blueprint-validation.ts`)
 * produces values shaped by `CardSlotReport`/`BlueprintTestResponse`, and the
 * frontend (`web/app-home/src/api/blueprints.schema.ts`) re-exports this same
 * schema to validate the response at the trust boundary — one definition, no
 * parallel hand-written type on either side (see "Schemas Are the Source of
 * Truth" in CLAUDE.md).
 */

import { z } from 'zod';

// Per-slot report

/**
 * One configured card slot's resolution against the sample data supplied to
 * `test`. `resolved` and `valueOk` are deliberately separate booleans, never
 * collapsed into one pass/fail flag — a slot can resolve to a value (the key
 * exists in the sample data) whose shape is still wrong (e.g. a `link` slot
 * holding a plain string instead of an array of `{url, text}`), and a
 * checks-strip rendering must be able to show both facts rather than one
 * green/red badge that hides which failed (Part C row 5's "never one green
 * badge" rule, restated here for the slot-level report it also governs).
 */
export const CardSlotReportSchema = z.object({
  /** The card slot name, e.g. `title`, `avatar`, `link`. */
  slot: z.string(),
  /** The data key this slot is bound to (`slotDataKey` of the configured `CardSlotValue`). */
  boundKey: z.string(),
  /** Whether `boundKey` resolved to a non-null value in the supplied sample data. */
  resolved: z.boolean(),
  /**
   * Whether the resolved value's shape is acceptable for this slot.
   * `false` whenever `resolved` is `false` (nothing to validate the shape
   * of); for a `link` slot, also `false` when the value fails
   * `validateLinkSampleShape`. Every other slot has no shape contract beyond
   * presence, so `valueOk === resolved` for them.
   */
  valueOk: z.boolean(),
  /** Human-readable outcome — the missing-data or shape-violation message, or a resolved-ok summary. */
  message: z.string(),
});
export type CardSlotReport = z.infer<typeof CardSlotReportSchema>;

// Detail-section key resolution (mirrors checkDetailKeyResolution's output)

export const UnresolvedDetailKeySchema = z.object({
  key: z.string(),
  label: z.string().optional(),
  didYouMean: z.string().optional(),
});
export type UnresolvedDetailKeyWire = z.infer<typeof UnresolvedDetailKeySchema>;

// Full `/:kind/test` response

export const BlueprintTestResponseSchema = z.object({
  /** Legacy title/subtitle-only validity flag — unchanged by this unit. */
  valid: z.boolean(),
  errors: z.array(z.string()).optional(),
  preview: z.object({
    title: z.string(),
    subtitle: z.string().optional(),
  }),
  /** Non-blocking, all-slots report — see `CardSlotReportSchema`. */
  slots: z.array(CardSlotReportSchema),
  /** Non-blocking detail-section key resolution — see `checkDetailKeyResolution`. */
  unresolvedDetailKeys: z.array(UnresolvedDetailKeySchema),
});
export type BlueprintTestResponse = z.infer<typeof BlueprintTestResponseSchema>;
